import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { 
  Paper, 
  TextField,
  Box,
  Typography,
  Menu,
  MenuItem,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  Select,
  ListItemIcon,
  ListItemText,
  TableContainer,
  IconButton,
  Tooltip,
  Button,
  Popover,
  Stack
} from '@mui/material';
import { Calculate as CalculateIcon, RemoveCircleOutline as RemoveCircleOutlineIcon, NotInterested as NotInterestedIcon } from '@mui/icons-material';
import { SHSIcon, RHSIcon, HEAIcon, IPEIcon } from './CrossSectionIcons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';

// Helper function to parse dimensions from section name (Ensure this is present)
const parseSectionName = (name) => {
  const matchCFSHS = name.match(/^CFSHS(\d+)X([\d.]+)/);
  if (matchCFSHS) {
    const height = parseInt(matchCFSHS[1], 10);
    const thickness = parseFloat(matchCFSHS[2]);
    return { height, width: height, thickness }; 
  }
  const matchCFRHS = name.match(/^CFRHS(\d+)X(\d+)X([\d.]+)/);
  if (matchCFRHS) {
    const height = parseInt(matchCFRHS[1], 10);
    const width = parseInt(matchCFRHS[2], 10);
    const thickness = parseFloat(matchCFRHS[3]);
    return { height, width, thickness };
  }
  const matchHFSHS = name.match(/^HFSHS(\d+)X([\d.]+)/); // Added HFSHS
  if (matchHFSHS) {
      const height = parseInt(matchHFSHS[1], 10);
      const thickness = parseFloat(matchHFSHS[2]);
      return { height, width: height, thickness };
  }
  const matchNumericSuffix = name.match(/^([A-Z]+)(\d+)/);
   if (matchNumericSuffix) {
     const height = parseInt(matchNumericSuffix[2], 10);
     // Attempt to infer width for HEA/HEB etc. if needed based on convention, else 0
     const typePrefix = matchNumericSuffix[1];
     const width = (typePrefix === 'HEA' || typePrefix === 'HEB') ? height : 0;
     return { height, width: width, thickness: 0 }; 
   }
  return { height: 0, width: 0, thickness: 0 };
};

// --- Custom Header Component for Wrapping ---
const WrappedHeader = (props) => {
  // props.colDef.headerName contains the original header text
  return (
    <div style={{ 
        whiteSpace: 'normal', // Allow wrapping
        lineHeight: 'normal', // Default line height
        textAlign: 'center', // Center header text
        padding: '4px 0' // Add some vertical padding
    }}>
        {props.colDef.headerName}
    </div>
  );
};

const CrossSectionsTable = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [filterType, setFilterType] = useState('CFSHS');
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const navigate = useNavigate();
  const dataGridContainerRef = useRef(null);

  // Fetch cross-section data from static JSON when filterType changes
  useEffect(() => {
    if (!filterType) return;
    const fetchSections = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/${filterType}_sections.json`);
        if (!response.ok) {
          throw new Error(`Failed to fetch sections for type ${filterType}`);
        }
        const data = await response.json();
        setSections(data);
      } catch (err) {
        setError('Failed to fetch cross sections.');
        setSections([]);
        console.error('Error fetching cross sections:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSections();
  }, [filterType]);

  // Filter cross sections based on search term only (no type filtering needed)
  const filteredSections = useMemo(() => {
    if (!filterText) return sections;
    const lowerText = filterText.toLowerCase();
    return sections.filter(s =>
      s.name.toLowerCase().includes(lowerText) ||
      String(s.height).includes(lowerText) ||
      String(s.width).includes(lowerText) ||
      String(s.thickness).includes(lowerText)
    );
  }, [sections, filterText]);

  // --- NATIVE EVENT LISTENER (MODIFIED TO TRIGGER REACT MENU) ---
  useEffect(() => {
    const containerElement = dataGridContainerRef.current;
    if (containerElement) {
      const handleNativeContextMenu = (event) => {
        const clickedElement = event.target;
        const rowElement = clickedElement.closest('.MuiDataGrid-row');

        if (rowElement) {
           console.log('>>> Native contextmenu event detected INSIDE DataGrid Row <<<', clickedElement);
           event.preventDefault();
           const rowId = rowElement.getAttribute('data-id');
           if (rowId) {
             console.log('>>> Extracted rowId:', rowId);
             setSelectedRowId(rowId);
             setContextMenu({ mouseX: event.clientX + 2, mouseY: event.clientY - 6 });
           } else {
             console.warn('>>> Could not find data-id on row element:', rowElement);
           }
        } else {
           const gridBody = containerElement.querySelector('.MuiDataGrid-virtualScroller');
           if (gridBody && gridBody.contains(clickedElement)) {
                console.log('>>> Native contextmenu event detected INSIDE DataGrid but NOT on a ROW <<<', clickedElement);
           } else {
                console.log('>>> Native contextmenu detected OUTSIDE DataGrid Body <<<', clickedElement);
           }
        }
      };

      containerElement.addEventListener('contextmenu', handleNativeContextMenu);
      console.log('>>> Native contextmenu listener attached (now triggers menu) <<<');

      return () => {
        if (containerElement) {
           containerElement.removeEventListener('contextmenu', handleNativeContextMenu);
           console.log('>>> Native contextmenu listener removed <<<');
        }
      };
    }
  }, [loading, error]); // Keep dependencies

  // --- REACT EVENT HANDLER (No longer used by DataGrid) ---
  // const handleContextMenu = (params, event) => {
  //   console.log("handleContextMenu (React) triggered - SHOULD NOT SEE THIS", params, event);
  // };

  // --- Menu Closing/Action Handlers (Still needed for the Menu component) ---
  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedRowId(null);
  };

  const handleCalculateWithSection = () => {
    if (selectedRowId !== null) {
      console.log(`Setting localStorage calculateCrossSectionId to: ${selectedRowId}`);
      localStorage.setItem('calculateCrossSectionId', selectedRowId.toString());
      handleCloseContextMenu();
      navigate('/formulae');
    }
  };

  // --- COLUMN DEFINITIONS (Use renderHeader for wrapping) ---
  const columns = [
    { field: 'id', headerName: 'ID', hide: true },
    { field: 'name', headerName: 'Name', width: 160, renderHeader: (params) => <WrappedHeader {...params} /> }, 
    { field: 'type', headerName: 'Type', hide: true },
    { field: 'height', headerName: 'Height\n(mm)', type: 'number', width: 40, renderHeader: (params) => <WrappedHeader {...params} /> }, 
    { field: 'width', headerName: 'Width\n(mm)', type: 'number', width: 40, renderHeader: (params) => <WrappedHeader {...params} /> }, 
    { field: 'thickness', headerName: 'Thickness\n(mm)', type: 'number', width: 40, renderHeader: (params) => <WrappedHeader {...params} /> }, 
    { field: 'area', headerName: 'A\n(mm²)', type: 'number', width: 70, renderHeader: (params) => <WrappedHeader {...params} /> }, 
    { field: 'moment_of_inertia_y', headerName: 'Iy\n(mm⁴)', type: 'number', width: 110, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'moment_of_inertia_z', headerName: 'Iz\n(mm⁴)', type: 'number', width: 110, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'polar_moment', headerName: 'Ip\n(mm⁴)', type: 'number', width: 100, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'radius_of_gyration_y', headerName: 'ry\n(mm)', type: 'number', width: 60, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'radius_of_gyration_z', headerName: 'rz\n(mm)', type: 'number', width: 60, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'radius_of_gyration_p', headerName: 'rp\n(mm)', type: 'number', width: 60, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'section_modulus_y', headerName: 'Sy\n(mm³)', type: 'number', width: 90, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'section_modulus_z', headerName: 'Sz\n(mm³)', type: 'number', width: 90, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'torsion_constant', headerName: 'It\n(mm⁴)', type: 'number', width: 90, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'torsion_modulus', headerName: 'Wt\n(mm³)', type: 'number', width: 90, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'plastic_modulus_y', headerName: 'Wpl,y\n(mm³)', type: 'number', width: 90, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'plastic_modulus_z', headerName: 'Wpl,z\n(mm³)', type: 'number', width: 90, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'weight_per_length', headerName: 'G\n(kg/m)', type: 'number', width: 60, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'surface_area_per_length', headerName: 'Am\n(m²/m)', type: 'number', width: 60, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'surface_area_to_volume', headerName: 'Am/V\n(m⁻¹)', type: 'number', width: 60, renderHeader: (params) => <WrappedHeader {...params} /> },
    { field: 'corner_radius', headerName: 'r₀\n(mm)', type: 'number', width: 40, renderHeader: (params) => <WrappedHeader {...params} /> },
  ];

  return (
    <Box sx={{ maxWidth: '95%', margin: '0 auto' }}>
      <div className="section" ref={dataGridContainerRef}>
        <Typography variant="h4" gutterBottom>
          Cross Sections Table
        </Typography>

        <Box display="flex" justifyContent="center" gap={2} mb={3}>
            <Tooltip title="Cold Formed SHS">
                <IconButton size="small" onClick={() => setFilterType('CFSHS')} color={filterType === 'CFSHS' ? 'primary' : 'default'}><SHSIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Cold Formed RHS">
                <IconButton size="small" onClick={() => setFilterType('CFRHS')} color={filterType === 'CFRHS' ? 'primary' : 'default'}><RHSIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Hot Formed SHS">
                <IconButton size="small" onClick={() => setFilterType('HFSHS')} color={filterType === 'HFSHS' ? 'primary' : 'default'}><SHSIcon /></IconButton>
            </Tooltip>
            <Tooltip title="HEA Sections">
                <IconButton size="small" onClick={() => setFilterType('HEA')} color={filterType === 'HEA' ? 'primary' : 'default'}><HEAIcon /></IconButton>
            </Tooltip>
            <Tooltip title="IPE Sections">
                <IconButton size="small" onClick={() => setFilterType('IPE')} color={filterType === 'IPE' ? 'primary' : 'default'}><IPEIcon /></IconButton>
            </Tooltip>
        </Box>

        <Box mb={3}>
          <TextField
            label="Filter by Name/Dimension"
            variant="outlined"
            size="small"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" my={5}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <DataGrid
            autoHeight
            rows={filteredSections}
            columns={columns}
            checkboxSelection={false}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false,
                  type: false,
                },
              },
            }}
            sx={{
              display: 'inline-block', 
              border: 1,
              borderColor: 'divider',
              '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  lineHeight: 'normal', 
                  py: 0.5,
                  px: 0.5,
              },
              '& .MuiDataGrid-cell': {
                  py: 0.5,
                  px: 0.5,
              },
              '& .MuiDataGrid-row': { cursor: 'context-menu' }
            }}
          />
        )}

        <Popover
          open={contextMenu !== null}
          anchorEl={contextMenu}
          onClose={handleCloseContextMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          slotProps={{
            paper: {
              sx: {
                p: 1,
                mt: 0.5
              }
            }
          }}
        >
          <Stack direction="row" spacing={1}>
            <Tooltip title="Calculate with this Section">
              <span>
                <IconButton
                  size="small"
                  onClick={handleCalculateWithSection}
                >
                  <CalculateIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Popover>
      </div>
    </Box>
  );
};

export default CrossSectionsTable; 